import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import ManageProjectDialog from "./ManageProjectDialog";
import ConfirmDeleteDialog from "./ConfirmDeleteDialog";
import { auth, db, storage } from "../firebase";
import { ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { doc, onSnapshot } from "firebase/firestore";

import { getFunctions, httpsCallable } from "firebase/functions";
import "./ProjectView.css";
import './Button.css';

import RenderOptionsDialog from "./RenderOptionsDialog";
import InsufficientCreditsDialog from "./InsufficientCreditsDialog";
import { trackEvent } from "../analytics";
import { statusTransitionEvent, editedVideoTransitionEvent, photoCompileTransitionEvent, photoCountBucket } from "../analytics-helpers";
import useAuthUser from "../hooks/useAuthUser";
import useEntitlements from "../hooks/useEntitlements";
import useEtaTracker from "../hooks/useEtaTracker";
import useContainerMirror from "../hooks/useContainerMirror";
import { computeEditedDurationSeconds } from "../utils/settingsControls";
import { creditsForMinutes, resolutionFromHeight } from "../utils/credits";
import { resolveSourceVideoPath, isSourcePreviewPending } from "../utils/videoSource";
import {
    seedEditDraft,
    hasClipJoins,
    buildRenderSettings,
    editDraftDiffersFromPersisted,
    isRenderInFlight,
    renderSegmentsForDraft,
    effectiveFadeFrames,
    captureGapCrossfadeCount,
    renderCrossfadeCount,
    watermarkRenderRequest,
    motionModeOf,
    pacedRenderPlan,
    MOTION_MODE_PACE,
} from "../lib/editDraft";
import { SPEED_CURVE_FIELD, speedCurveMatchesSettings } from "../lib/motionPace";
import useFeatureFlag from "../hooks/useFeatureFlag";
import { featureFlagOf, featureFlagDefaultOf, VARIABLE_SPEED } from "../lib/operations";
import ProjectLegacyLayout from "./project/ProjectLegacyLayout";
import ProjectTabs from "./project/ProjectTabs";
import OverviewTab from "./project/OverviewTab";
import FootageTab from "./project/FootageTab";
import EditsTab from "./project/EditsTab";

// The backend raises FAILED_PRECONDITION for several reasons (TOS not
// accepted, insufficient credits). The structured `details.reason` is the
// authoritative discriminator; the message-prefix check is a fallback for
// older deployments that don't include details.
const isInsufficientCreditsError = (error) => {
    if (!error) return false;
    if (error.code !== 'functions/failed-precondition') return false;
    if (error.details?.reason === 'INSUFFICIENT_CREDITS') return true;
    return typeof error.message === 'string' && error.message.startsWith('Insufficient credits');
};

const ProjectView = ({ project, saveName, onDelete, credits, activeTab, onTabChange, containerV2 }) => {
    // Drives the render dialog's watermark switch (#344). Server-side
    // enforcement still happens in the worker — this only decides what the
    // control looks like.
    const entitlements = useEntitlements(useAuthUser());
    // users/{uid}.watermarkDefaults — the account's last-used watermark
    // customization, hydrated into the draft below for fields the project's
    // own settings don't claim. Null until the users/{uid} snapshot lands.
    //
    // ENTITLEMENT-GATED HERE, deliberately. The API only writes that blob for
    // accounts holding `watermarkRemoval`, but entitlement can LAPSE afterwards
    // — a Pro account that downgrades keeps its saved look — which is why the
    // API's own comment says its presence is not a licence. Without this gate a
    // lapsed account's card would open on a custom logo at a custom corner the
    // worker is going to ignore, and the receipt would promise a render nobody
    // gets.
    const watermarkDefaults = entitlements.watermarkRemoval
        ? (entitlements.watermarkDefaults || null)
        : null;
    // The `motionPace` flag, read HERE as well as in EditorPanel because the
    // Motion section's DEFAULT mode is part of the seed and the seed lives in
    // this component. Both the flag name and its dark fallback come from the
    // operation registry, so this is the same gate the resolver and the worker
    // apply rather than a string that happens to match. Same doc, one Firestore
    // listener under the SDK.
    //
    // It arrives ASYNC (config/featureFlags is a snapshot), reading `false`
    // until it lands — so the seeding effect below re-seeds once when it does,
    // under the same user-touched guard the watermark defaults use.
    const { enabled: motionPaceEnabled } = useFeatureFlag(
        featureFlagOf(VARIABLE_SPEED),
        { defaultValue: featureFlagDefaultOf(VARIABLE_SPEED) },
    );
    const [nameEntry, setName] = useState(project.projectName);
    const [showManageDialog, setShowManageDialog] = useState(false);
    const [showConfirmDeleteDialog, setShowConfirmDeleteDialog] = useState(false);
    const [showRenderOptions, setShowRenderOptions] = useState(false);
    const [currentProject, setCurrentProject] = useState(project);
    const [isRecalculating, setIsRecalculating] = useState(false);
    const [isCreating, setIsCreating] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [isAnalysisPending, setIsAnalysisPending] = useState(false);
    const [localVideoMeta, setLocalVideoMeta] = useState(null);
    const [videoUrl, setVideoUrl] = useState(null);
    // #478 — "the browser could not decode this source". Owned here, not in a
    // tab, for the same reason `proxyStatus` is gated in resolveSourceVideoPath:
    // every playback surface renders behind `videoUrl`, so one verdict up here
    // hides them together. Held in a tab it would only fix that tab and leave
    // the others (Edits renders its player during analysis) showing the black
    // box the placeholder exists to replace.
    const [sourceErrored, setSourceErrored] = useState(false);
    const [editedVideoUrl, setEditedVideoUrl] = useState(null);
    const [shouldScrollToProgress, setShouldScrollToProgress] = useState(false);
    const [showConfirmStopDialog, setShowConfirmStopDialog] = useState(false);
    const [stoppingTask, setStoppingTask] = useState(null);
    const [insufficientCredits, setInsufficientCredits] = useState(null);
    // #401 — the Edits tab's operation cards. Owned here (not in the cards)
    // for two reasons: it has to survive a tab switch, and handleConfirmRender
    // is what persists it.
    // The seed options matter on THIS call, not just in the effect below: for a
    // project opened with its settings already written (the normal case — the
    // analyzer commits them before the Edits tab is useful) every re-seed branch
    // below is guarded shut, so this is the only seed that project ever gets.
    // Passing them here is what lets `defaultsToJoinFades` see a real
    // `hasRender`; `motionPaceEnabled` is deliberately left out because the flag
    // arrives async and has its own re-seed branch.
    const [editDraft, setEditDraft] = useState(() => seedEditDraft(project.settings, undefined, {
        hasRender: !!project.editedVideo?.path || isRenderInFlight(project.editedVideo),
        video: project.video,
    }));
    // Bookkeeping for the seeding effect below. The project doc is written on
    // every render progress tick, so the draft cannot simply follow settings or
    // an in-progress render would wipe a switch the user just flipped. It is
    // seeded once per project, plus once more if the project had no settings
    // yet, plus once more when the project's clip joins first appear, plus once
    // more when the account's watermarkDefaults land (the users/{uid} snapshot
    // is async), plus once more when the `motionPace` flag resolves — and every
    // re-seed is skipped if the user has already edited.
    const draftSeedRef = useRef({
        projectId: project.id,
        hasSettings: !!project.settings,
        hasClipJoins: hasClipJoins(project.video),
        hasWatermarkDefaults: false,
        motionPaceEnabled: false,
        userTouched: false,
    });
    const videoPlayerRef = useRef(null);
    const progressRef = useRef(null);
    const prevVideoStatusRef = useRef(null);
    const prevEditedStatusRef = useRef(null);
    const prevPhotoCompileStatusRef = useRef(null);
    const statusRefsInitializedRef = useRef(false);
    const sourcePlayedRef = useRef(false);
    const editedPlayedRef = useRef(false);

    // Phase 0 container-v2 mirror: lazily copy legacy project.video /
    // project.editedVideo into videos/main + edits/timelapse subdocs. No UI
    // impact — reads the live snapshot and best-effort writes on the side.
    useContainerMirror(currentProject);

    const analysisEta = useEtaTracker(
        currentProject.video?.progress,
        currentProject.video?.status === 'analyzing'
    );
    const generationEta = useEtaTracker(
        currentProject.editedVideo?.progress,
        currentProject.editedVideo?.status === 'processing'
    );

    useEffect(() => {
        setCurrentProject(project);
    }, [project]);

    // Seeds the operation cards from the project on screen.
    //
    // Reads `project.settings` — the PROP — not `currentProject.settings`.
    // `currentProject` is state that the effect above copies from the prop, so
    // on the render where `project.id` changes it still holds the PREVIOUS
    // project; seeding from it showed project B's cards carrying project A's
    // trim/deflicker/transition state, and the next render persisted A's
    // settings onto B. Both the id and the settings come from the same prop, so
    // they can never be one render out of step with each other. Studio's
    // projects query is a live snapshot, so this prop also tracks Firestore.
    useEffect(() => {
        const settings = project.settings;
        const seed = draftSeedRef.current;
        // The Motion section's default-mode inputs, read fresh on every seed.
        //
        // `hasRender` is only ever read at the moment a seed happens. It is in
        // the dependency list because the rule requires it, NOT as a re-seed
        // trigger: every branch below is guarded on its own bit flipping, so an
        // `editedVideo` progress tick re-runs this effect and changes nothing.
        // Re-seeding on it would throw away the draft the user just rendered
        // from, the instant the render was queued.
        // NOT `!!project.editedVideo`: that object survives a render that
        // FAILED (or was cancelled), and a project whose FIRST render failed
        // has no video on the page at all — the marker never clears, so such a
        // project would sit on MotionTrim forever while every sibling opens on
        // MotionPace. `editedVideo.path` is the field that actually means "a
        // video is on screen": it is written only by the completing
        // transaction and never cleared, which also covers a failed OVERWRITE,
        // where the status reads `failed` but the PREVIOUS render is still
        // what the page is playing.
        const edited = project.editedVideo;
        const motionDefaults = {
            motionPaceEnabled,
            hasRender: !!edited?.path || isRenderInFlight(edited),
            // `defaultsToJoinFades` reads `concatJoinFrames` off this to keep the
            // clip-join default to projects the flag can actually change.
            video: project.video,
        };
        if (seed.projectId !== project.id) {
            // A different project is on screen. `userTouched` is per-project
            // bookkeeping, so it resets here — otherwise one edit anywhere would
            // permanently disable seeding for every project opened afterwards.
            draftSeedRef.current = {
                projectId: project.id,
                hasSettings: !!settings,
                hasClipJoins: hasClipJoins(project.video),
                hasWatermarkDefaults: !!watermarkDefaults,
                motionPaceEnabled,
                userTouched: false,
            };
            setEditDraft(seedEditDraft(settings, watermarkDefaults, motionDefaults));
            return;
        }
        // The project had no settings when it was opened (a new project — the
        // analyzer writes the whole blob, including transition_length_frames, in
        // the same commit that flips video.status to 'analyzed'). Seed from it
        // now that it exists — but only if the cards are still showing defaults.
        // The Edits tab is usable DURING analysis, so re-seeding unconditionally
        // would reset switches the user flipped in exactly the window this
        // feature opens up.
        if (!seed.hasSettings && settings && !seed.userTouched) {
            draftSeedRef.current = { ...draftSeedRef.current, hasSettings: true };
            setEditDraft(seedEditDraft(settings, watermarkDefaults, motionDefaults));
        }
        // The project's clip joins arrive async, and can arrive LONG after the
        // only seed the branches above would ever give it. The storage finalize
        // trigger writes `video.concatJoinFrames` whenever a combined video
        // lands (functions storage background main.py), and it fires again on a
        // RE-combine — someone adding a second clip to a project that analyzed
        // as a single file. That project already has settings, so no branch
        // above re-seeds, and the clip-join fade default (which requires joins
        // to exist, so it did not apply at open time) would sit unticked for
        // exactly the user it was written for until a page reload. One re-seed
        // when joins FIRST appear, same shape and same user-touched guard as the
        // async branches below. Marked consumed either way, so a later
        // re-combine never re-seeds mid-edit.
        if (!draftSeedRef.current.hasClipJoins && hasClipJoins(project.video)) {
            const touched = draftSeedRef.current.userTouched;
            draftSeedRef.current = { ...draftSeedRef.current, hasClipJoins: true };
            if (!touched) {
                setEditDraft(seedEditDraft(project.settings, watermarkDefaults, motionDefaults));
            }
        }
        // The account's last-used watermark customization arrives async off
        // the users/{uid} snapshot. One re-seed when it first lands, under the
        // same user-touched guard — a corner picked before the snapshot landed
        // must not be reverted by it. Marked consumed either way, so a later
        // defaults write (another render) never re-seeds mid-edit.
        if (!draftSeedRef.current.hasWatermarkDefaults && watermarkDefaults) {
            const touched = draftSeedRef.current.userTouched;
            draftSeedRef.current = { ...draftSeedRef.current, hasWatermarkDefaults: true };
            if (!touched) {
                setEditDraft(seedEditDraft(project.settings, watermarkDefaults, motionDefaults));
            }
        }
        // The `motionPace` flag arrives async too (config/featureFlags), and it
        // is what decides the Motion section's default mode — so the first seed
        // of a page load always runs with it dark. One re-seed when the value
        // CHANGES, same shape and same user-touched guard as the watermark
        // defaults above: a mode picked before the flag landed must not be
        // reverted by it. Tracked as a value rather than a "seen" bit so a flag
        // turned back OFF mid-session also restores today's MotionTrim default,
        // which is what makes the kill switch actually a kill switch.
        if (draftSeedRef.current.motionPaceEnabled !== motionPaceEnabled) {
            const touched = draftSeedRef.current.userTouched;
            draftSeedRef.current = { ...draftSeedRef.current, motionPaceEnabled };
            if (!touched) {
                setEditDraft(seedEditDraft(project.settings, watermarkDefaults, motionDefaults));
            }
        }
    }, [project.id, project.settings, project.video, project.editedVideo, watermarkDefaults, motionPaceEnabled]);

    const handleEditDraftChange = useCallback((patch) => {
        // Marks the draft as the user's, so the seeding effect above stops
        // treating it as replaceable defaults.
        draftSeedRef.current = { ...draftSeedRef.current, userTouched: true };
        setEditDraft((prev) => ({ ...prev, ...patch }));
    }, []);

    useEffect(() => {
        // Reset per-project state when the project changes.
        prevVideoStatusRef.current = null;
        prevEditedStatusRef.current = null;
        prevPhotoCompileStatusRef.current = null;
        statusRefsInitializedRef.current = false;
        sourcePlayedRef.current = false;
        editedPlayedRef.current = false;
        const unsub = onSnapshot(doc(db, "projects", project.id), (doc) => {
            const data = doc.data();
            const nextVideoStatus = data?.video?.status || null;
            const nextEditedStatus = data?.editedVideo?.status || null;
            const nextPhotoCompileStatus = data?.photoCompile?.status || null;
            if (!statusRefsInitializedRef.current) {
                prevVideoStatusRef.current = nextVideoStatus;
                prevEditedStatusRef.current = nextEditedStatus;
                prevPhotoCompileStatusRef.current = nextPhotoCompileStatus;
                statusRefsInitializedRef.current = true;
            } else {
                const vEvent = statusTransitionEvent(prevVideoStatusRef.current, nextVideoStatus);
                if (vEvent) trackEvent(vEvent, { project_id: project.id });
                const eEvent = editedVideoTransitionEvent(prevEditedStatusRef.current, nextEditedStatus);
                if (eEvent) trackEvent(eEvent, { project_id: project.id });
                const pcEvent = photoCompileTransitionEvent(prevPhotoCompileStatusRef.current, nextPhotoCompileStatus);
                if (pcEvent) {
                    const photoCount = data?.photoCompile?.photoCount;
                    trackEvent(pcEvent, {
                        project_id: project.id,
                        photo_count: photoCount ?? null,
                        count_bucket: photoCountBucket(photoCount),
                        error_code: nextPhotoCompileStatus === 'failed' ? (data?.photoCompile?.errorCode || null) : undefined,
                    });
                }
                prevVideoStatusRef.current = nextVideoStatus;
                prevEditedStatusRef.current = nextEditedStatus;
                prevPhotoCompileStatusRef.current = nextPhotoCompileStatus;
            }
            setCurrentProject({ ...data, id: project.id });
            if (data?.video) {
                setIsAnalysisPending(false);
            }
        });
        return () => unsub();
    }, [project.id]);

    // The storage path the players are pointed at. A new path is a new verdict
    // — and the proxy landing IS a path change — so a stale decode failure must
    // not keep the placeholder up over a source that now plays. Keyed on the
    // resolved path rather than on `videoUrl`, because withholding `videoUrl`
    // below is itself a change to it: keying on the URL would clear the flag
    // that caused the withholding, and the player would flip back on.
    const sourceVideoPath = resolveSourceVideoPath(currentProject.video);
    useEffect(() => { setSourceErrored(false); }, [sourceVideoPath]);

    useEffect(() => {
        setName(currentProject.projectName);
        if (sourceVideoPath) {
            const storageRef = ref(storage, sourceVideoPath);
            getDownloadURL(storageRef).then(setVideoUrl).catch(() => setVideoUrl(null));
        } else {
            setVideoUrl(null);
        }
        if (currentProject.editedVideo?.path) {
            const path = currentProject.editedVideo.path;
            if (path.startsWith('http')) {
                setEditedVideoUrl(path);
            } else {
                const storageRef = ref(storage, path);
                getDownloadURL(storageRef).then(setEditedVideoUrl).catch(() => setEditedVideoUrl(null));
            }
        } else {
            setEditedVideoUrl(null);
        }
        // sourceVideoPath is derived from currentProject, so it adds no extra
        // runs — it is listed only to satisfy exhaustive-deps.
    }, [currentProject, sourceVideoPath]);

    const isPreviewPending = isSourcePreviewPending(currentProject.video, {
        sourceErrored,
        isAnalysisPending,
    });
    // Same lever `proxyStatus` already pulls: withhold the URL and every
    // surface behind `{videoUrl && ...}` hides at once.
    const playableVideoUrl = isPreviewPending ? null : videoUrl;
    const handleSourceError = useCallback(() => setSourceErrored(true), []);

    const handleDelete = () => {
        setShowManageDialog(false);
        setShowConfirmDeleteDialog(true);
    };

    const handleConfirmDelete = () => {
        trackEvent('project_deleted', { project_id: project.id });
        onDelete(project.id);
        setShowConfirmDeleteDialog(false);
    };

    const handleSaveName = (id, newName) => {
        trackEvent('project_renamed', { project_id: id });
        saveName(id, newName);
    };

    const handleRecalculate = useCallback(async (newSettings) => {
        setIsRecalculating(true);
        console.log("Recalculating with settings:", { projectId: project.id, settings: newSettings });
        try {
            const functions = getFunctions();
            const recalculateMotionSegments = httpsCallable(functions, 'recalculate_motion_segments');
            const result = await recalculateMotionSegments({ projectId: project.id, settings: newSettings });
            const { effectiveMinThreshold, effectiveMaxThreshold } = result.data || {};
            if (effectiveMinThreshold != null && effectiveMaxThreshold != null) {
                setCurrentProject(prev => ({
                    ...prev,
                    video: { ...prev.video, effectiveMinThreshold, effectiveMaxThreshold }
                }));
            }
        } catch (error) {
            console.error("Error recalculating motion segments:", error);
            alert("An error occurred while recalculating. Please try again.");
        }
        setIsRecalculating(false);
    }, [project.id]);

    useEffect(() => {
        if (shouldScrollToProgress && progressRef.current) {
            progressRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
            setShouldScrollToProgress(false);
        }
    }, [shouldScrollToProgress, isCreating]);

    const scrollToProgress = () => {
        setShouldScrollToProgress(true);
    };

    const handleGenerationError = (error) => {
        console.error("Error creating video:", error);
        trackEvent('generation_error', { project_id: project.id, error: error.message });
        if (isInsufficientCreditsError(error)) {
            const requiredFromError = Number(error?.details?.requiredCredits);
            setInsufficientCredits({
                requiredCredits: Number.isFinite(requiredFromError)
                    ? requiredFromError
                    : estimateCreditCost(currentProject),
                balance: Number.isFinite(credits?.balance) ? credits.balance : null,
            });
            trackEvent('insufficient_credits_blocked', { project_id: project.id });
            return;
        }
        alert("An error occurred while creating the video. Please try again.");
    };

    // The photo compile charges credits too, and it lives two components down
    // (FootageTab → PhotoCompileCta) rather than behind a dialog this
    // component owns. Handing it this callback routes its shortfall into the
    // SAME InsufficientCreditsDialog the render path uses, instead of growing
    // a second, weaker "you're out of credits" affordance.
    const handleCompileInsufficientCredits = useCallback((requiredCredits) => {
        const required = Number(requiredCredits);
        setInsufficientCredits({
            requiredCredits: Number.isFinite(required) ? required : null,
            balance: Number.isFinite(credits?.balance) ? credits.balance : null,
        });
        trackEvent('insufficient_credits_blocked', { project_id: project.id, source: 'photo_compile' });
    }, [credits?.balance, project.id]);

    const handleCreateVideo = () => {
        if (isRenderInFlight(currentProject.editedVideo)) return;
        trackEvent('render_dialog_opened', { project_id: project.id });
        setShowRenderOptions(true);
    };

    const handleSourcePlay = () => {
        if (sourcePlayedRef.current) return;
        sourcePlayedRef.current = true;
        trackEvent('preview_played', { project_id: project.id, video_type: 'source' });
    };

    const handleEditedPlay = () => {
        if (editedPlayedRef.current) return;
        editedPlayedRef.current = true;
        trackEvent('preview_played', { project_id: project.id, video_type: 'edited' });
    };

    const handleConfirmRender = async () => {
        if (isCreating) return;
        // The project this render belongs to, captured BEFORE the first await.
        // Every callable below is fired against it, but the user can click
        // another project while they are in flight — and `setCurrentProject`'s
        // updater then receives the NEW project as `prev`. Writing the merged
        // blob into it unguarded put project A's settings on project B, where
        // only B's next Firestore write would correct it (for an idle project,
        // possibly never). B's next render would then compare its draft against
        // A's settings, decide "unchanged", skip a persist B actually needed and
        // render from B's stale stored settings. Same class of bug as the
        // editDraft seeding leak this component already guards against.
        const targetProjectId = project.id;
        const isOverwrite = currentProject.editedVideo?.status === 'complete';
        const persisted = currentProject.settings || {};
        // A trim_stillness flip only exists in the enable layer, and the worker
        // reads that off the project document — so this comparison is what makes
        // "keep the full length" actually reach the render, not just the UI.
        const needsPersistPrefs = editDraftDiffersFromPersisted(persisted, editDraft);

        setIsCreating(true);
        try {
            const functions = getFunctions();

            if (needsPersistPrefs) {
                // The operation contract's enable layer (#400), with the cards'
                // explicit switch states layered on (#401). Everything else stays
                // projected from the tuning keys, so a stale block from an earlier
                // render can't outrank what is on screen now. Entitlement-gated
                // operations stay a REQUEST here; the worker re-resolves them
                // against the account.
                const mergedSettings = buildRenderSettings(persisted, editDraft);
                // Still the settings-persist path, NOT a motion recalculation:
                // with trim_stillness off the callable skips the picker entirely
                // (requires_motion_analysis is false) and just stores the blob.
                // The full-length segment itself is synthesized by the worker
                // once ffprobe has read the source — never here, and never from
                // the picker parameters, which are meaningless for it.
                const recalculateMotionSegments = httpsCallable(functions, 'recalculate_motion_segments');
                await recalculateMotionSegments({ projectId: targetProjectId, settings: mergedSettings });
                // Only the project this write was for may take it. On a switch
                // the local copy is left alone: the callable already persisted
                // the blob to A server-side, and A's own snapshot delivers it
                // the next time A is on screen.
                setCurrentProject((prev) => (
                    prev?.id === targetProjectId ? { ...prev, settings: mergedSettings } : prev
                ));
            }

            if (isOverwrite) {
                const deleteEditedVideo = httpsCallable(functions, 'delete_edited_video');
                await deleteEditedVideo({ projectId: targetProjectId });
            }

            const generateEditedVideo = httpsCallable(functions, 'generate_edited_video');
            // A request, not a decision — the worker re-checks entitlements and
            // watermarks anyway if the account isn't entitled (#343). Sent as
            // the user's PREFERENCE: an over-optimistic `true` from a client
            // that is wrong about the account costs nothing, while the
            // effective value would send an explicit `false` the worker HONOURS
            // for an entitled account — a paid render with a wordmark burned in.
            // The watermark customization (logo/corner/size/opacity) rides the
            // same call — all four keys always, incl. an explicit null logoId,
            // so re-selecting Tweedia clears a saved custom logo server-side.
            await generateEditedVideo({
                projectId: targetProjectId,
                ...watermarkRenderRequest(editDraft),
            });
            trackEvent('generate_video', { project_id: targetProjectId, is_overwrite: isOverwrite });

            setShowRenderOptions(false);
            scrollToProgress();
        } catch (error) {
            handleGenerationError(error);
        } finally {
            setIsCreating(false);
        }
    };

    const handleFileChange = async (event) => {
        if (project.video) {
            alert("A video already exists for this project.");
            return;
        }

        const file = event.target.files[0];
        if (!file) {
            return;
        }

        const user = auth.currentUser;
        if (!user) {
            return;
        }

        // Extract metadata from the video file client-side so we can show it immediately
        const videoEl = document.createElement('video');
        videoEl.preload = 'metadata';
        const objectUrl = URL.createObjectURL(file);
        const cleanup = () => URL.revokeObjectURL(objectUrl);
        videoEl.addEventListener('loadedmetadata', () => {
            setLocalVideoMeta({
                length: videoEl.duration,
                resolution: { width: videoEl.videoWidth, height: videoEl.videoHeight },
            });
            cleanup();
        }, { once: true });
        videoEl.addEventListener('error', cleanup, { once: true });
        videoEl.src = objectUrl;

        setUploading(true);
        setUploadProgress(0);
        setIsAnalysisPending(false);

        const fileSizeMb = Math.round(((file.size || 0) / (1024 * 1024)) * 10) / 10;
        const uploadStartMs = Date.now();
        trackEvent('upload_started', { project_id: project.id, file_size_mb: fileSizeMb });

        const storageRef = ref(storage, `videos/${user.uid}/${project.id}/${file.name}`);
        const uploadTask = uploadBytesResumable(storageRef, file);

        uploadTask.on('state_changed',
            (snapshot) => {
                const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
                setUploadProgress(Math.round(progress));
            },
            (error) => {
                console.error("Error uploading file:", error);
                setUploading(false);
                const msg = ((error && error.message) || String(error)).slice(0, 100);
                trackEvent('upload_failed', { project_id: project.id, error: msg });
            },
            () => {
                setUploading(false);
                setIsAnalysisPending(true);
                const uploadSeconds = Math.round(((Date.now() - uploadStartMs) / 1000) * 10) / 10;
                trackEvent('upload_completed', {
                    project_id: project.id,
                    file_size_mb: fileSizeMb,
                    upload_seconds: uploadSeconds,
                });
            }
        );
    };

    // Shared duration computation used by both the displayed "Edited Duration"
    // and the credit cost estimate, so the UI stays in sync with backend billing
    // (see packages/functions/billing/background/main.py). Uses the same helper
    // as the render dialog so the two can never diverge.
    //
    // Reads the DRAFT, not the persisted keys, so the readout answers "what
    // will this render be" while the user is still flipping switches — with
    // stillness trimming off that is the whole source as one segment (#401).
    //
    // With capture-gap crossfades on, the worker cuts the segment list open at
    // `video.captureGapFrames` before it renders anything, and each cut costs
    // the output one transition's worth of frames. Applying the same split here
    // is what keeps the duration readout and the render dialog's receipt honest
    // — most detected gaps never actually split (see renderSegmentsForDraft).
    // The Clips stat and the transitions card count the SAME helper, so the
    // receipt and the cards can never describe two different renders.
    const renderSegments = useMemo(
        () => renderSegmentsForDraft(currentProject.video, editDraft),
        [currentProject.video, editDraft]
    );

    // The one reconciliation of the two fade declarations — capture gaps and
    // combined-video clip joins — into the single list and single switch the
    // worker renders from. Every count below reads it, so the receipt cannot
    // describe a different set of fades than `renderSegments` was cut for.
    const fadeBoundaries = useMemo(
        () => effectiveFadeFrames(currentProject.video, editDraft),
        [currentProject.video, editDraft]
    );

    // How many boundaries the render actually fades. Analysis cuts a segment at
    // every capture gap, so a multi-day project reaches the worker with a
    // boundary per gap — and those hard-cut unless a fade switch is on.
    // Counting `renderSegments.length - 1` instead would charge each of them a
    // transition's worth of overlap the render never pays.
    const appliedCrossfadeCount = useMemo(
        () => renderCrossfadeCount(
            renderSegments,
            fadeBoundaries.frames,
            fadeBoundaries.enabled,
            editDraft.transitionFrames ?? 0
        ),
        [renderSegments, fadeBoundaries, editDraft.transitionFrames]
    );

    // The crossfades the switches actually buy, for the render dialog's receipt.
    const appliedCaptureGapCount = useMemo(
        () => captureGapCrossfadeCount(
            renderSegments,
            fadeBoundaries.frames,
            fadeBoundaries.enabled,
            editDraft.transitionFrames ?? 0
        ),
        [renderSegments, fadeBoundaries, editDraft.transitionFrames]
    );

    // Which motion operation this draft RESOLVES to — the same answer the render
    // will get. Derived once and read by both the duration readout below and the
    // render dialog's receipt, so the card and the receipt can never disagree
    // about which operation is running. `motionModeOf` applies the feature gate
    // itself: while `motionPace` is dark a draft asking for pace resolves to
    // TRIM here exactly as it would server-side.
    const motionMode = useMemo(
        () => motionModeOf(editDraft, motionPaceEnabled),
        [editDraft, motionPaceEnabled]
    );

    // MotionTrim and MotionPace measure their output in completely different
    // units, so a pace render's clip list has to be rebuilt in the paced video's
    // own output frames before anything can be measured. `pacedRenderPlan` does
    // that — and returns null when the curve cannot answer (a project analyzed
    // before curve seeding, #573), which is the signal to fall back to the trim
    // segment math rather than state a confident 0:00:00.
    //
    // ONE plan for the card and the render receipt, so the two can never quote
    // different lengths or different crossfade counts for the same render.
    const pacePlan = useMemo(
        () => (motionMode === MOTION_MODE_PACE
            ? pacedRenderPlan(currentProject.video, editDraft)
            : null),
        [motionMode, currentProject.video, editDraft]
    );

    // Whether the stored curve still describes the settings on screen.
    //
    // The curve is recomputed SERVER-SIDE on save, so a `maxSpeed`/`smoothness`
    // drag leaves the persisted record describing the PREVIOUS setting for the
    // ~1-3s of debounce plus round trip — and indefinitely if that call fails.
    // Max speed scales output length directly, so quoting the stored curve in
    // that window states a length this render will not produce. Worse at the
    // receipt than on the card: confirming a render persists the draft first
    // (`handleConfirmRender`), which recomputes the curve and then renders from
    // the NEW one, so the number on the dialog would be a promise about a curve
    // that no longer exists.
    //
    // Three conditions, the first two of which the pace chart uses too
    // (`MotionSection.js`, `MotionPaceChart.js`): a recompute in flight, or a
    // stored curve whose own clamped settings differ from the draft's.
    //
    // THE THIRD IS NO CURVE AT ALL, and it is not the narrow legacy case it
    // looks like. `seed_speed_curve` (analyzevideo/processing/motion_analysis.py)
    // returns None when `motionPace` was dark at analysis time, so every project
    // analyzed before the flag was lit has no curve — and `defaultsToPace`
    // (lib/editDraft.js) then puts every never-rendered one of them on MotionPace
    // with `trimStillness: false` the moment it IS lit. Without this line those
    // projects fall through to the trim segment math, which with trimming off is
    // the FULL 1x source length, and state it as fact while the walk will run at
    // up to `MAX_SPEED_DEFAULT`. That is a several-fold overstatement with no
    // hedge — exactly the failure this readout exists to remove, and #574's
    // oversized projects land in the same hole.
    //
    // So a curve-less pace project gets the `--` + "Length updates once the new
    // speed curve is calculated." affordance instead. That also matches what the
    // user already sees two elements up: the pace chart draws its own "no curve
    // yet" placeholder for this project, so the chart and the stat cell now
    // admit the same ignorance rather than one quoting a number.
    const persistedSpeedCurve = currentProject.video?.[SPEED_CURVE_FIELD];
    const paceDurationPending = useMemo(() => {
        if (motionMode !== MOTION_MODE_PACE) return false;
        if (isRecalculating) return true;
        if (!pacePlan) return true;
        return !!persistedSpeedCurve
            && !speedCurveMatchesSettings(
                persistedSpeedCurve, editDraft.maxSpeed, editDraft.smoothness,
            );
    }, [
        motionMode,
        isRecalculating,
        pacePlan,
        persistedSpeedCurve,
        editDraft.maxSpeed,
        editDraft.smoothness,
    ]);

    // The Clips cell sits beside Edited Duration, and the two must answer for
    // the SAME render — `clipCount`'s own comment says so. A paced render splits
    // at the capture-gap boundaries REMAPPED into the paced video's output
    // frames, where a gap leaving a 100-frame source tail can be only 25 output
    // frames and hard-cut instead of splitting. Counting the source segments
    // therefore reports a clip the render does not produce, and the receipt next
    // to it would state a different number of crossfades.
    //
    // Null while the length is `--`, so the two cells go quiet together rather
    // than one confidently counting clips off a curve the other just disowned.
    // A single paced clip also cannot show a crossfade, which keeps the
    // transitions card's "no effect" note honest under MotionPace.
    const pacedClipCount = pacePlan ? pacePlan.segments.length : null;

    const editedDurationSeconds = useMemo(() => {
        if (pacePlan) {
            return computeEditedDurationSeconds(
                pacePlan.segments,
                pacePlan.outputFps,
                editDraft.transitionFrames ?? 0,
                pacePlan.crossfadeCount
            );
        }
        return computeEditedDurationSeconds(
            renderSegments,
            currentProject.settings?.output_fps || 30,
            editDraft.transitionFrames ?? 0,
            appliedCrossfadeCount
        );
    }, [
        pacePlan,
        renderSegments,
        currentProject.settings?.output_fps,
        editDraft.transitionFrames,
        appliedCrossfadeCount,
    ]);

    const calculateEditedDuration = (durationInSeconds) => {
        if (durationInSeconds <= 0) return "0:00:00";

        const h = Math.floor(durationInSeconds / 3600).toString();
        const m = Math.floor(durationInSeconds % 3600 / 60).toString().padStart(2, '0');
        const s = Math.floor(durationInSeconds % 60).toString().padStart(2, '0');
        return `${h}:${m}:${s}`;
    };

    const estimateCreditCost = (project) => {
        if (!project.video?.length) return null;

        const durationMinutes = (project.video.length || 0) / 60.0;
        const resolution = resolutionFromHeight(project.video?.resolution?.height || 1080);

        // Pricing rules require a minimum charge of 1 credit for any video
        // generation (see Terms of Service). `creditsForMinutes` returns null
        // for inputs that can't produce a number, which must not surface as
        // NaN/Infinity in the UI.
        const raw = creditsForMinutes(durationMinutes, resolution);
        return raw === null ? 0 : Math.max(1, raw);
    };

    const handleStopTask = async (taskType) => {
        // Guard against re-entrancy: clicks before React commits the state
        // update from setStoppingTask can otherwise fire multiple in-flight
        // cancellation requests for the same task.
        if (stoppingTask) return;
        setShowConfirmStopDialog(false);
        setStoppingTask(taskType);
        try {
            const functions = getFunctions();
            const cancelTask = httpsCallable(functions, 'cancel_task');
            await cancelTask({ projectId: project.id, taskType });
            trackEvent('cancel_task', { project_id: project.id, task_type: taskType });
        } catch (error) {
            console.error("Error cancelling task:", error);
            trackEvent('cancel_task_error', { project_id: project.id, task_type: taskType, error: error.message });
            alert("An error occurred while stopping the task. Please try again.");
        }
        setStoppingTask(null);
    };

    // Common props threaded into both the legacy layout and the tab views.
    // All state lives in this component so switching tabs mid-upload or
    // mid-render never interrupts anything in flight.
    const layoutProps = {
        currentProject,
        isAnalysisPending,
        localVideoMeta,
        uploading,
        uploadProgress,
        onFileChange: handleFileChange,
        analysisEta,
        generationEta,
        stoppingTask,
        onStopClick: (t) => setShowConfirmStopDialog(t),
        projectId: project.id,
        // #478: the withheld URL, so no surface can render a source we already
        // know the browser cannot decode.
        videoUrl: playableVideoUrl,
        isPreviewPending,
        onSourceError: handleSourceError,
        editedVideoUrl,
        videoPlayerRef,
        progressRef,
        onSourcePlay: handleSourcePlay,
        onEditedPlay: handleEditedPlay,
        onRecalculate: handleRecalculate,
        isRecalculating,
        editedDurationSeconds,
        // The stat card shows `--` instead of a number while the stored speed
        // curve is behind the controls — see `paceDurationPending`.
        editedDurationPending: paceDurationPending,
        // Null on a trim render, where the editor counts the source segments
        // itself — see `pacedClipCount`.
        pacedClipCount,
        calculateEditedDuration,
        estimateCreditCost,
        credits,
        isCreating,
        onCreateVideo: handleCreateVideo,
        onCompileInsufficientCredits: handleCompileInsufficientCredits,
        // #350: EditorPanel's shortfall warning is the other pre-render price
        // preview, so it gets the same credits upsell as the render dialog.
        entitlements,
        // #401 operation cards.
        editDraft,
        onEditDraftChange: handleEditDraftChange,
    };

    const tabHandler = (tab) => {
        if (typeof onTabChange !== 'function') return;
        trackEvent('studio_tab_viewed', { project_id: project.id, tab });
        onTabChange(tab);
    };

    return (
        <div style={{ bottomPadding: "20px" }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <h3 className="project-name-header">
                    {nameEntry}
                </h3>
                <button
                    type="button"
                    className="pv-settings-button"
                    onClick={() => setShowManageDialog(true)}
                    aria-label="Project settings"
                >
                    <svg
                        className="pv-settings-icon"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        aria-hidden="true"
                    >
                        <circle cx="12" cy="12" r="3" />
                        <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
                    </svg>
                    <span className="pv-settings-label">Settings</span>
                </button>
            </div>
            {containerV2 ? (
                <>
                    <ProjectTabs activeTab={activeTab || 'overview'} onChange={tabHandler} />
                    {(activeTab || 'overview') === 'overview' && (
                        <OverviewTab {...layoutProps} onTabChange={tabHandler} />
                    )}
                    {activeTab === 'footage' && (
                        <FootageTab {...layoutProps} onTabChange={tabHandler} />
                    )}
                    {activeTab === 'edits' && (
                        <EditsTab {...layoutProps} onTabChange={tabHandler} />
                    )}
                </>
            ) : (
                <ProjectLegacyLayout {...layoutProps} />
            )}
            {showManageDialog &&
                <ManageProjectDialog
                    project={currentProject}
                    onClose={() => setShowManageDialog(false)}
                    onSaveName={handleSaveName}
                    onDelete={handleDelete}
                />}
            {showConfirmDeleteDialog &&
                <ConfirmDeleteDialog
                    onConfirm={handleConfirmDelete}
                    onCancel={() => setShowConfirmDeleteDialog(false)}
                />}
            {showRenderOptions &&
                <RenderOptionsDialog
                    fps={currentProject.video?.fps}
                    // A pace render's clips are the PACED video's frames at the
                    // paced rate (`pacedRenderPlan`), not the source's — so the
                    // dialog measures that list with the same arithmetic it
                    // applies to a trim render, and needs no pace-specific math.
                    motionSegments={pacePlan ? pacePlan.segments : renderSegments}
                    outputFps={pacePlan
                        ? pacePlan.outputFps
                        : (currentProject.settings?.output_fps || currentProject.video?.fps)}
                    transitionFrames={editDraft.transitionFrames}
                    deflicker={editDraft.deflicker}
                    removeWatermark={editDraft.removeWatermark}
                    watermarkLogoId={editDraft.watermarkLogoId ?? null}
                    watermarkLogoName={editDraft.watermarkLogoName || null}
                    watermarkPosition={editDraft.watermarkPosition}
                    // The receipt names the mode the render will run (#590).
                    // Resolved, not read off the draft: under a dark flag a
                    // MotionPace recipe renders as something else, and the
                    // receipt has to state what actually happens.
                    motionMode={motionModeOf(editDraft, motionPaceEnabled)}
                    motionPaceEnabled={motionPaceEnabled}
                    captureGapTransitions={fadeBoundaries.enabled}
                    // Not the raw switch: `joinsApply` is true only when this
                    // project actually HAS clip joins, which is what decides
                    // whether the receipt calls these boundaries clip joins or
                    // capture gaps.
                    clipJoinTransitions={fadeBoundaries.joinsApply}
                    // Both counts come from the same plan as the segments above:
                    // a boundary that survives in source frames can be too short
                    // to fade once the walk has sprinted through it, so a paced
                    // render's counts are its own.
                    captureGapCount={pacePlan ? pacePlan.captureGapCount : appliedCaptureGapCount}
                    crossfadeCount={pacePlan ? pacePlan.crossfadeCount : appliedCrossfadeCount}
                    // Suppresses the length statement while the stored curve is
                    // behind the controls: confirming here persists the draft
                    // and re-renders from a curve that does not exist yet.
                    durationPending={paceDurationPending}
                    creditCost={estimateCreditCost(currentProject)}
                    balance={Number.isFinite(credits?.balance) ? credits.balance : null}
                    isOverwrite={currentProject.editedVideo?.status === 'complete'}
                    busy={isCreating}
                    canRemoveWatermark={!!entitlements.watermarkRemoval}
                    entitlementsLoading={!!entitlements.loading}
                    onConfirm={handleConfirmRender}
                    onCancel={() => setShowRenderOptions(false)}
                    projectId={project.id}
                    entitlements={entitlements}
                    subscription={entitlements.subscription}
                />}
            {showConfirmStopDialog &&
                <ConfirmDeleteDialog
                    title={showConfirmStopDialog === 'analysis' ? 'Stop Analysis?' : 'Stop Generation?'}
                    message="Are you sure? This will cancel the current task and you'll be able to restart it anytime."
                    confirmLabel="Yes, Stop"
                    onConfirm={() => handleStopTask(showConfirmStopDialog)}
                    onCancel={() => setShowConfirmStopDialog(false)}
                />}
            {insufficientCredits &&
                <InsufficientCreditsDialog
                    requiredCredits={insufficientCredits.requiredCredits}
                    balance={insufficientCredits.balance}
                    onCancel={() => setInsufficientCredits(null)}
                    onBuyCredits={() => {
                        trackEvent('buy_credits_cta_clicked', { project_id: project.id, source: 'insufficient_credits_dialog' });
                        setInsufficientCredits(null);
                    }}
                    entitlements={entitlements}
                    subscription={entitlements.subscription}
                    projectId={project.id}
                />}
        </div>
    )
}

export default ProjectView;